import { FormEvent, useEffect, useMemo, useState } from "react";
import { api } from "../api";
import type { DarfStandardGroup, RevenueCode } from "../types";

type Flash = { kind: "ok" | "error"; text: string } | null;
type Props = { canManage: boolean; flash: (value: Flash) => void; onAssignments: () => void };
type Draft = {
  name: string; lead_days: number; revenue_codes: string[];
  enabled: boolean; competence: string; due_date: string; due_date_source: string;
};

const previousMonth = new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1);
const initialMonth = `${previousMonth.getFullYear()}-${String(previousMonth.getMonth() + 1).padStart(2, "0")}`;
const displayDate = (value: string | null) => value ? value.split("-").reverse().join("/") : "Pendente";

export default function DarfStandardsPage({ canManage, flash, onAssignments }: Props) {
  const [month, setMonth] = useState(initialMonth);
  const competence = `${month.slice(5, 7)}/${month.slice(0, 4)}`;
  const [groups, setGroups] = useState<DarfStandardGroup[]>([]);
  const [catalog, setCatalog] = useState<RevenueCode[]>([]);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [codeQuery, setCodeQuery] = useState("");
  const [origin, setOrigin] = useState("");
  const [saving, setSaving] = useState(false);

  async function load() {
    const result = await api<{ items: DarfStandardGroup[] }>(`/api/darf-standards?competence=${encodeURIComponent(competence)}`);
    setGroups(result.items);
  }
  useEffect(() => { load().catch((error) => flash({ kind: "error", text: error.message })); }, [competence]);
  useEffect(() => {
    api<{ items: RevenueCode[] }>("/api/darf-plans/catalog")
      .then((result) => setCatalog(result.items))
      .catch((error) => flash({ kind: "error", text: error.message }));
  }, []);

  const eligible = useMemo(() => catalog.filter((item) => item.document_type.toUpperCase() !== "DAE"), [catalog]);
  const origins = useMemo(() => [...new Set(eligible.map((item) => item.origin))], [eligible]);
  const matchingCodes = useMemo(() => eligible.filter((item) =>
    (!origin || item.origin === origin) &&
    (!codeQuery || `${item.code} ${item.description}`.toLowerCase().includes(codeQuery.toLowerCase()))
  ).slice(0, 80), [eligible, origin, codeQuery]);

  function newGroup() {
    setEditingId(null);
    setDraft({ name: "", lead_days: 0, revenue_codes: [], enabled: true,
      competence, due_date: "", due_date_source: "" });
    setCodeQuery("");
  }
  function edit(group: DarfStandardGroup) {
    setEditingId(group.id);
    setDraft({ name: group.name, lead_days: group.lead_days, revenue_codes: group.revenue_codes,
      enabled: group.enabled, competence,
      due_date: group.due_date || "", due_date_source: group.due_date_source || "" });
  }
  function toggleCode(code: string) {
    if (!draft) return;
    const selected = new Set(draft.revenue_codes);
    if (selected.has(code)) selected.delete(code); else selected.add(code);
    setDraft({ ...draft, revenue_codes: [...selected].sort() });
  }
  async function save(event: FormEvent) {
    event.preventDefault();
    if (!draft?.revenue_codes.length) {
      flash({ kind: "error", text: "Selecione ao menos um código de receita." }); return;
    }
    if (draft.due_date && !draft.due_date_source) {
      flash({ kind: "error", text: "Informe a fonte do vencimento efetivo." }); return;
    }
    setSaving(true);
    try {
      await api(editingId ? `/api/darf-standards/${editingId}` : "/api/darf-standards", {
        method: editingId ? "PUT" : "POST",
        body: JSON.stringify({ ...draft, due_date: draft.due_date || null,
          due_date_source: draft.due_date_source || null }),
      });
      await load();
      setDraft(null);
      flash({ kind: "ok", text: "Configuração do grupo salva. Aplique-o às empresas na etapa seguinte." });
    } catch (error) {
      flash({ kind: "error", text: error instanceof Error ? error.message : "Falha ao salvar grupo" });
    } finally { setSaving(false); }
  }

  return <>
    <div className="page-head"><div>
      <p className="eyebrow">PROGRAMAÇÃO COMPARTILHADA</p>
      <h1>Grupos padrão DARF</h1>
      <p>Defina códigos, vencimento e antecedência. A adesão das empresas é gerenciada separadamente.</p>
    </div><div className="actions"><button className="secondary" onClick={onAssignments}>Aplicar às empresas</button>{canManage && <button className="primary" onClick={newGroup}>+ Novo grupo padrão</button>}</div></div>
    <div className="notice">Etapa 1 de 2: configure o grupo. Depois use “Aplicar às empresas” para definir participantes. Os grupos ainda não disparam emissões.</div>
    <section className="card darf-filters"><label>Competência para vencimento
      <input type="month" value={month} onChange={(event) => { setMonth(event.target.value); setDraft(null); }} />
    </label></section>
    <section className="card table-card">{groups.length ? <table><thead><tr>
      <th>Grupo padrão</th><th>Vencimento</th><th>Buscar a partir de</th><th>Empresas</th><th>Códigos</th><th>Situação</th><th />
    </tr></thead><tbody>{groups.map((group) => <tr key={group.id}>
      <td><strong>{group.name}</strong><small>{group.due_date_source ? <a href={group.due_date_source} target="_blank" rel="noreferrer">Ver fonte do vencimento ↗</a> : "Vencimento não informado para esta competência"}</small></td>
      <td>{displayDate(group.due_date)}</td>
      <td>{displayDate(group.search_from)}<small>{group.lead_days} dia(s) corridos antes</small></td>
      <td>{group.company_ids.length}<small>{group.company_ids.length ? "Gerenciar em Aplicar às empresas" : "Nenhuma empresa vinculada"}</small></td>
      <td>{group.revenue_codes.length}<small>Ver lista em Editar</small></td>
      <td><span className="pill inactive">{group.enabled ? "Em preparação" : "Desativado"}</span></td>
      <td>{canManage && <button className="table-action" onClick={() => edit(group)}>Editar</button>}</td>
    </tr>)}</tbody></table> : <div className="empty"><p>Nenhum grupo padrão cadastrado.</p></div>}</section>
    {draft && canManage && <section className="card darf-editor"><h2>{editingId ? "Editar grupo padrão" : "Novo grupo padrão"}</h2>
      <form className="form-grid" onSubmit={save}>
        <div className="form-section wide"><h3>1. Configuração do grupo</h3><p>Defina o nome, a antecedência e o vencimento desta competência.</p></div>
        <label>Nome do grupo<input required minLength={2} maxLength={120} value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Ex.: MIT — PIS/Cofins mensal" /></label>
        <label>Buscar quantos dias corridos antes<input required type="number" min="0" max="31" value={draft.lead_days} onChange={(e) => setDraft({ ...draft, lead_days: Number(e.target.value) })} /></label>
        <label>Vencimento efetivo de {competence}<input type="date" value={draft.due_date} onChange={(e) => setDraft({ ...draft, due_date: e.target.value })} /></label>
        <label>Fonte do vencimento<input type="url" required={!!draft.due_date} value={draft.due_date_source} onChange={(e) => setDraft({ ...draft, due_date_source: e.target.value })} placeholder="Agenda Tributária ou Sicalc" /></label>
        <label className="check-label wide"><input type="checkbox" checked={draft.enabled} onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })} /> Grupo habilitado para futura automação</label>
        <div className="form-section wide"><h3>2. Códigos de receita ({draft.revenue_codes.length})</h3><p>Selecione a composição do grupo. Códigos exclusivos de DAE não entram.</p></div>
        <label>Origem<select value={origin} onChange={(e) => setOrigin(e.target.value)}><option value="">Todas</option>{origins.map((item) => <option key={item}>{item}</option>)}</select></label>
        <label>Buscar código ou descrição<input value={codeQuery} onChange={(e) => setCodeQuery(e.target.value)} placeholder="Ex.: 1082" /></label>
        <div className="wide darf-codes">{matchingCodes.map((item) => <label key={item.code} className="darf-code">
          <input type="checkbox" checked={draft.revenue_codes.includes(item.code)} onChange={() => toggleCode(item.code)} />
          <span><strong>{item.code}</strong> · {item.origin}<small>{item.description}</small></span>
        </label>)}</div>
        {draft.revenue_codes.length > 0 && <p className="wide darf-selected">Selecionados: {draft.revenue_codes.join(", ")}</p>}
        <footer className="wide"><button type="button" className="secondary" onClick={() => setDraft(null)}>Cancelar</button><button className="primary" disabled={saving}>{saving ? "Salvando…" : "Salvar grupo padrão"}</button></footer>
      </form>
    </section>}
  </>;
}
